import { FeeAmount, TICK_SPACINGS } from 'elephantswapv3-sdk'

type PriceStrategyKey = 'stable' | 'wide' | 'one-sided-lower' | 'one-sided-upper'

interface PriceStrategy {
  description: string
  display: string
  key: PriceStrategyKey
  range: [number, number]
  title: string
}

const trimDecimal = (value: string): string =>
  value.includes('.') ? value.replace(/0+$/, '').replace(/\.$/, '') : value

export function formatRangePrice(value: number): string {
  if (!Number.isFinite(value) || value <= 0) return ''

  const precise = value.toPrecision(15)
  if (!/[eE]/.test(precise)) return trimDecimal(precise)

  const [coefficient, exponentValue] = precise.toLowerCase().split('e')
  const exponent = Number(exponentValue)
  const digits = coefficient.replace('.', '')
  const originalDecimalIndex = coefficient.includes('.') ? coefficient.indexOf('.') : coefficient.length
  const decimalIndex = originalDecimalIndex + exponent

  if (decimalIndex <= 0) {
    return trimDecimal(`0.${'0'.repeat(-decimalIndex)}${digits}`)
  }
  if (decimalIndex >= digits.length) {
    return `${digits}${'0'.repeat(decimalIndex - digits.length)}`
  }
  return trimDecimal(`${digits.slice(0, decimalIndex)}.${digits.slice(decimalIndex)}`)
}

export function getPriceStrategies(currentPrice: number, feeAmount: FeeAmount): PriceStrategy[] {
  const tickSpacing = TICK_SPACINGS[feeAmount]
  const oneTickFactor = Math.pow(1.0001, tickSpacing)
  const stableFactor = Math.pow(1.0001, 3 * tickSpacing)
  const stablePercent = ((stableFactor - 1) * 100).toFixed(2).replace(/\.?0+$/, '')

  return [
    {
      key: 'stable',
      title: 'Stable',
      display: `± ${stablePercent}%`,
      description: 'Good for stablecoins or low volatility pairs',
      range: [currentPrice / stableFactor, currentPrice * stableFactor],
    },
    {
      key: 'wide',
      title: 'Wide',
      display: '−50% — +100%',
      description: 'Good for volatile pairs',
      range: [currentPrice * 0.5, currentPrice * 2],
    },
    {
      key: 'one-sided-lower',
      title: 'One-sided lower',
      display: '−50%',
      description: 'Supply liquidity if price goes down',
      range: [currentPrice * 0.5, currentPrice / oneTickFactor],
    },
    {
      key: 'one-sided-upper',
      title: 'One-sided upper',
      display: '+100%',
      description: 'Supply liquidity if price goes up',
      range: [currentPrice * oneTickFactor, currentPrice * 2],
    },
  ]
}

export function matchesPriceStrategy(
  range: [number, number] | undefined,
  strategy: PriceStrategy,
  feeAmount: FeeAmount
): boolean {
  if (!range || range.some((price) => !Number.isFinite(price) || price <= 0)) return false

  // The existing inputs snap to usable ticks. Allow one spacing of drift when
  // recognizing the card after those callbacks have normalized its values.
  const tolerance = Math.log(1.0001) * TICK_SPACINGS[feeAmount] * 1.05
  return range.every((price, index) => Math.abs(Math.log(price / strategy.range[index])) <= tolerance)
}
